<template>
  <HrPageLayout
    :title="pageTitle"
    :subtitle="pageSubtitle"
    icon="💰"
    :breadcrumb-items="[
      { label: 'Home', route: '/dashboard' },
      { label: 'Arrears and Recoveries' },
      { label: pageTitle }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-secondary" @click="load">
        <i class="bx bx-refresh"></i> Refresh
      </button>
      <button type="button" class="hr-btn hr-btn-primary" @click="openCreate">
        <i class="bx bx-plus"></i> {{ isRecovery ? 'New recovery' : 'New arrears' }}
      </button>
    </template>

    <div class="filters-card">
      <div class="filters-grid">
        <label>
          <span>Department</span>
          <select v-model="filterDept" class="hr-select" @change="load">
            <option value="">All departments</option>
            <option v-for="d in departments" :key="d.department_id || d.department_name" :value="d.department_name">
              {{ d.department_name }}
            </option>
          </select>
        </label>
        <label>
          <span>Type</span>
          <select v-model="filterType" class="hr-select" @change="load">
            <option value="">All types</option>
            <option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
          </select>
        </label>
        <label>
          <span>Search</span>
          <input v-model="filterQ" class="hr-input" placeholder="Name, employee number or National ID" @keyup.enter="load">
        </label>
      </div>
    </div>

    <div class="table-wrap">
      <table class="table table-sm modern-table">
        <thead>
          <tr>
            <th>Employee number</th>
            <th>Name</th>
            <th>Department</th>
            <th>Type</th>
            <th class="text-end">Amount</th>
            <th>Computation doc</th>
            <th>Schedule report</th>
            <th>Payment file</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in cases" :key="row.id">
            <td>{{ row.emp_no }}</td>
            <td>{{ row.employee_name }}</td>
            <td>{{ row.department_name }}</td>
            <td>{{ row.type_label }}</td>
            <td class="text-end">{{ money(row.principal) }}</td>
            <td>
              <template v-if="row.computation_doc_path">
                <a :href="fileUrl(row.computation_doc_path)" target="_blank" class="link-btn">View</a>
                <a :href="fileUrl(row.computation_doc_path)" download class="link-btn">Download</a>
              </template>
              <button v-else type="button" class="link-btn" @click="pickFile(row, 'computation')">Upload</button>
            </td>
            <td>
              <template v-if="Number(row.is_liability)">
                <span class="text-muted">N/A (pay-in slip)</span>
              </template>
              <template v-else>
                <button type="button" class="link-btn" @click="openSchedule(row, true)">View</button>
                <button type="button" class="link-btn" @click="openSchedule(row, false)">Download</button>
              </template>
            </td>
            <td>
              <template v-if="Number(row.is_liability)">
                <button type="button" class="link-btn" @click="pickFile(row, 'payin')">Upload pay-in slip</button>
                <a v-if="row.payin_slip_path" :href="fileUrl(row.payin_slip_path)" target="_blank" class="link-btn">Download</a>
              </template>
              <template v-else>
                <button type="button" class="link-btn" @click="openPayments(row, true)">View report</button>
                <button type="button" class="link-btn" @click="openPayments(row, false)">Download</button>
                <button type="button" class="link-btn" @click="pickFile(row, 'payment')">Upload</button>
                <a v-if="row.payment_file_path" :href="fileUrl(row.payment_file_path)" target="_blank" class="link-btn">File</a>
              </template>
            </td>
            <td>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-primary" style="margin-bottom: 4px; display: block;" @click="openPay(row)">Record payment</button>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" style="margin-bottom: 4px; display: block;" @click="openPaymentHistory(row)">View payments</button>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-secondary" style="margin-bottom: 4px; display: block;" @click="openEdit(row)">Edit record</button>
              <button type="button" class="hr-btn hr-btn-sm hr-btn-danger" style="display: block;" @click="deleteCase(row)">Delete</button>
            </td>
          </tr>
          <tr v-if="!cases.length">
            <td colspan="8" class="text-center text-muted">No records found.</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="showCreate" class="modal-backdrop" @click.self="showCreate = false">
      <div class="modal-card">
        <h3>{{ isRecovery ? 'Record recovery' : 'Record arrears' }}</h3>
        <label>Search employee (name, employee number or National ID)
          <input v-model="empQ" class="hr-input" @input="searchEmp">
        </label>
        <ul v-if="empHits.length" class="hits">
          <li v-for="e in empHits" :key="e.id" @click="selectEmp(e)">
            {{ e.fullName }} · {{ e.empNo }} · ID {{ e.nationalId }}
          </li>
        </ul>
        <div v-if="form.employee_id" class="auto-box">
          <div><strong>{{ form.employee_name }}</strong></div>
          <div>Emp No: {{ form.emp_no }} · National ID: {{ form.national_id }}</div>
          <div>Department: {{ form.department_name }} · {{ form.designation }}</div>
        </div>
        <label>Type
          <select v-model="form.type_code" class="hr-select">
            <option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
          </select>
        </label>
        <label v-if="isOthers">Specify
          <input v-model="form.type_other" class="hr-input" placeholder="Specify">
        </label>
        <label>Principal (KES)
          <input v-model.number="form.principal" type="number" min="0" step="0.01" class="hr-input">
        </label>
        <template v-if="!isLiability">
          <label>Amount paid / recovered per month (KES)
            <input v-model.number="form.monthly_amount" type="number" min="0" step="0.01" class="hr-input">
          </label>
          <label>Start month
            <input v-model="form.start_month" type="month" class="hr-input">
          </label>
          <p class="hint">Months: <strong>{{ monthsPreview }}</strong> (principal ÷ monthly amount)</p>
        </template>
        <template v-else>
          <p class="hint">Liabilities are paid once. Recovery method: pay-in slip (upload after saving).</p>
        </template>
        <label v-if="isRecovery && !isLiability">Recovery method
          <input v-model="form.recovery_method" class="hr-input" placeholder="e.g. payroll deduction">
        </label>
        <label>Computation document (optional)
          <input type="file" ref="computationDocInput" class="hr-input" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png">
        </label>
        <div class="modal-actions">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showCreate = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveCase">Save</button>
        </div>
      </div>
    </div>

    <div v-if="showEdit" class="modal-backdrop" @click.self="showEdit = false">
      <div class="modal-card">
        <h3>{{ isRecovery ? 'Edit recovery' : 'Edit arrears' }}</h3>
        <div class="auto-box">
          <div><strong>{{ editForm.employee_name }}</strong></div>
          <div>Emp No: {{ editForm.emp_no }} · National ID: {{ editForm.national_id }}</div>
        </div>
        <label>Type
          <select v-model="editForm.type_code" class="hr-select">
            <option v-for="t in typeOptions" :key="t" :value="t">{{ t }}</option>
          </select>
        </label>
        <label v-if="String(editForm.type_code || '').toLowerCase().includes('others')">Specify
          <input v-model="editForm.type_other" class="hr-input" placeholder="Specify">
        </label>
        <label>Principal (KES)
          <input v-model.number="editForm.principal" type="number" min="0" step="0.01" class="hr-input" :disabled="editForm.paid_total > 0">
        </label>
        <template v-if="!(isRecovery && editForm.type_code === 'Liabilities')">
          <label>Amount paid / recovered per month (KES)
            <input v-model.number="editForm.monthly_amount" type="number" min="0" step="0.01" class="hr-input" :disabled="editForm.paid_total > 0">
          </label>
          <label>Start month
            <input v-model="editForm.start_month" type="month" class="hr-input" :disabled="editForm.paid_total > 0">
          </label>
        </template>
        <label v-if="isRecovery && !(isRecovery && editForm.type_code === 'Liabilities')">Recovery method
          <input v-model="editForm.recovery_method" class="hr-input" placeholder="e.g. payroll deduction">
        </label>
        <label>Notes
          <textarea v-model="editForm.notes" class="hr-input" rows="2"></textarea>
        </label>
        <p v-if="editForm.paid_total > 0" class="hint" style="color: #dc2626;">Financials are locked because payments exist.</p>
        <div class="modal-actions">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showEdit = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="saveEdit">Save Changes</button>
        </div>
      </div>
    </div>

    <div v-if="showPay" class="modal-backdrop" @click.self="showPay = false">
      <div class="modal-card">
        <h3>Record new payment</h3>
        <p v-if="activeCase">{{ activeCase.employee_name }} · Balance {{ money(activeCase.balance) }}</p>
        <label>{{ isRecovery ? 'Recovery month' : 'Month' }}
          <input v-model="pay.period_label" class="hr-input" placeholder="e.g. Apr 2026">
        </label>
        <label v-if="isRecovery">Recovery method
          <input v-model="pay.method" class="hr-input" placeholder="Pay-in slip / payroll">
        </label>
        <label>{{ isRecovery ? 'Date recovered' : 'Date paid' }}
          <input v-model="pay.paid_at" type="date" class="hr-input">
        </label>
        <label>{{ isRecovery ? 'Amount recovered' : 'Amount paid' }}
          <input v-model.number="pay.amount" type="number" min="0" step="0.01" class="hr-input">
        </label>
        <label>Evidence document (optional)
          <input type="file" ref="evidenceDocInput" class="hr-input" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png">
        </label>
        <p class="hint">New balance will be previous balance minus this amount.</p>
        <div class="modal-actions">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showPay = false">Cancel</button>
          <button type="button" class="hr-btn hr-btn-primary" :disabled="saving" @click="savePay">Save payment</button>
        </div>
      </div>
    </div>

    <div v-if="showPaymentHistory" class="modal-backdrop" @click.self="showPaymentHistory = false">
      <div class="modal-card" style="width: min(800px, 100%);">
        <h3>Payment History</h3>
        <p v-if="activeCase">{{ activeCase.employee_name }} · Balance {{ money(activeCase.balance) }}</p>
        <div class="table-wrap">
          <table class="table table-sm modern-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Month</th>
                <th>Method</th>
                <th class="text-end">Amount</th>
                <th class="text-end">Balance</th>
                <th>Evidence</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="p in paymentHistory" :key="p.id">
                <td>{{ p.paid_at }}</td>
                <td>{{ p.period_label }}</td>
                <td>{{ p.method }}</td>
                <td class="text-end">{{ money(p.amount) }}</td>
                <td class="text-end">{{ money(p.balance_after) }}</td>
                <td>
                  <template v-if="p.evidence_doc_path">
                    <a :href="fileUrl(p.evidence_doc_path)" target="_blank" class="link-btn">View</a>
                    <a :href="fileUrl(p.evidence_doc_path)" download class="link-btn">Download</a>
                  </template>
                  <span v-else class="text-muted">None</span>
                </td>
              </tr>
              <tr v-if="!paymentHistory.length">
                <td colspan="6" class="text-center text-muted">No payments found.</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="modal-actions mt-3">
          <button type="button" class="hr-btn hr-btn-secondary" @click="showPaymentHistory = false">Close</button>
        </div>
      </div>
    </div>
    <input ref="fileInput" type="file" class="d-none" @change="onFile">
  </HrPageLayout>
</template>

<script>
import { authUrl, uploads, execute, toast } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { exportArSchedulePdf, exportArPaymentReportPdf } from "@/utils/arrearsRecoveriesPdf";

const ARREARS_TYPES = [
  "Promotion Arrears",
  "Salary Underpayment Arrears",
  "Salary Stoppage/Reinstatement Arrears",
  "Lifting of Interdiction",
  "Delayed UPN Issuance",
  "Re-designation Arrears",
  "Others (Specify)",
];
const RECOVERY_TYPES = ["Liabilities", "Overpayment Recoveries", "Others (Specify)"];

export default {
  name: "ArrearsRecoveriesCases",
  components: { HrPageLayout },
  props: {
    kind: { type: String, default: "arrears" },
  },
  data() {
    return {
      cases: [],
      departments: [],
      filterDept: "",
      filterType: "",
      filterQ: "",
      showCreate: false,
      showEdit: false,
      showPay: false,
      showPaymentHistory: false,
      saving: false,
      empQ: "",
      empHits: [],
      searchTimer: null,
      form: this.emptyForm(),
      editForm: {},
      pay: { period_label: "", method: "", paid_at: "", amount: 0 },
      paymentHistory: [],
      activeCase: null,
      uploadMode: "payment",
      uploadId: 0,
    };
  },
  computed: {
    isRecovery() {
      return this.kind === "recovery";
    },
    pageTitle() {
      return this.isRecovery ? "Recoveries" : "Arrears";
    },
    pageSubtitle() {
      return this.isRecovery
        ? "Liabilities (pay-in slip) and overpayment recoveries with schedules"
        : "Promotion and salary arrears with payment schedules";
    },
    typeOptions() {
      return this.isRecovery ? RECOVERY_TYPES : ARREARS_TYPES;
    },
    isOthers() {
      return String(this.form.type_code || "").toLowerCase().includes("others");
    },
    isLiability() {
      return this.isRecovery && this.form.type_code === "Liabilities";
    },
    monthsPreview() {
      const p = Number(this.form.principal) || 0;
      const m = Number(this.form.monthly_amount) || 0;
      if (p <= 0 || m <= 0) return "—";
      return Math.ceil(p / m);
    },
  },
  mounted() {
    this.load();
  },
  watch: {
    kind() {
      this.filterDept = "";
      this.filterType = "";
      this.filterQ = "";
      this.showCreate = false;
      this.showEdit = false;
      this.showPay = false;
      this.load();
    },
  },
  methods: {
    emptyForm() {
      return {
        employee_id: "",
        employee_name: "",
        emp_no: "",
        national_id: "",
        department_name: "",
        designation: "",
        type_code: this.isRecovery ? "Overpayment Recoveries" : "Promotion Arrears",
        type_other: "",
        principal: 0,
        monthly_amount: 0,
        start_month: "",
        recovery_method: "",
      };
    },
    money(n) {
      return (Number(n) || 0).toLocaleString("en-KE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    fileUrl(path) {
      if (!path) return "#";
      const base = String(uploads || "").replace(/\/+$/, "");
      return `${base}/${String(path).replace(/^\/+/, "")}`;
    },
    async load() {
      const fd = new FormData();
      fd.append("function", "arListCases");
      fd.append("kind", this.isRecovery ? "recovery" : "arrears");
      fd.append("department", this.filterDept);
      fd.append("type", this.filterType);
      fd.append("q", this.filterQ);
      const res = await execute(fd, authUrl);
      const d = res?.data?.data || {};
      this.cases = d.cases || [];
      this.departments = d.departments || [];
    },
    openCreate() {
      this.form = this.emptyForm();
      this.empHits = [];
      this.empQ = "";
      this.showCreate = true;
    },
    searchEmp() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(async () => {
        if (String(this.empQ).trim().length < 2) {
          this.empHits = [];
          return;
        }
        const fd = new FormData();
        fd.append("function", "searchArEmployees");
        fd.append("q", this.empQ.trim());
        try {
          const res = await execute(fd, authUrl);
          if (res?.data?.success === false) {
            this.empHits = [];
            toast.error(res?.data?.message || "Employee search failed");
            return;
          }
          this.empHits = res?.data?.data?.employees || [];
        } catch (e) {
          this.empHits = [];
          toast.error("Employee search failed");
        }
      }, 300);
    },
    selectEmp(e) {
      this.form.employee_id = e.id;
      this.form.employee_name = e.fullName;
      this.form.emp_no = e.empNo;
      this.form.national_id = e.nationalId;
      this.form.department_name = e.departmentName;
      this.form.designation = e.positionTitle || e.designation || "";
      this.empHits = [];
      this.empQ = e.fullName;
    },
    async saveCase() {
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "arCreateCase");
      fd.append("kind", this.isRecovery ? "recovery" : "arrears");
      Object.entries(this.form).forEach(([k, v]) => fd.append(k, v == null ? "" : String(v)));
      if (this.$refs.computationDocInput && this.$refs.computationDocInput.files[0]) {
        fd.append("computation_doc", this.$refs.computationDocInput.files[0]);
      }
      try {
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success(res.data.message || "Saved");
          this.showCreate = false;
          await this.load();
        } else {
          toast.error(res?.data?.message || "Save failed");
        }
      } finally {
        this.saving = false;
      }
    },
    async openEdit(row) {
      const pack = await this.fetchCase(row.id);
      if (!pack?.case) {
        toast.error("Failed to load record details");
        return;
      }
      const c = pack.case;
      this.editForm = {
        id: c.id,
        employee_name: c.employee_name,
        emp_no: c.emp_no,
        national_id: c.national_id,
        type_code: c.type_code,
        type_other: c.type_other || "",
        principal: Number(c.principal) || 0,
        monthly_amount: Number(c.monthly_amount) || 0,
        start_month: String(c.start_month || "").substring(0, 7),
        recovery_method: c.recovery_method || "",
        notes: c.notes || "",
        paid_total: Number(c.paid_total) || 0,
      };
      this.showEdit = true;
    },
    async saveEdit() {
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "arUpdateCase");
      Object.entries(this.editForm).forEach(([k, v]) => fd.append(k, v == null ? "" : String(v)));
      try {
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success(res.data.message || "Updated");
          this.showEdit = false;
          await this.load();
        } else {
          toast.error(res?.data?.message || "Update failed");
        }
      } finally {
        this.saving = false;
      }
    },
    async deleteCase(row) {
      if (!confirm(`Are you sure you want to delete this record for ${row.employee_name}? This will remove the case and any recorded payments.`)) return;
      const fd = new FormData();
      fd.append("function", "arDeleteCase");
      fd.append("id", String(row.id));
      try {
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success(res.data.message || "Deleted");
          await this.load();
        } else {
          toast.error(res?.data?.message || "Delete failed");
        }
      } catch (e) {
        toast.error("An error occurred while deleting");
      }
    },
    async fetchCase(id) {
      const fd = new FormData();
      fd.append("function", "arGetCase");
      fd.append("id", String(id));
      const res = await execute(fd, authUrl);
      return res?.data?.data || null;
    },
    async openSchedule(row, view) {
      const pack = await this.fetchCase(row.id);
      if (!pack?.case) return;
      await exportArSchedulePdf({ caze: pack.case, schedule: pack.schedule, view });
    },
    async openPayments(row, view) {
      const pack = await this.fetchCase(row.id);
      if (!pack?.case) return;
      await exportArPaymentReportPdf({ caze: pack.case, payments: pack.payments, view });
    },
    openPay(row) {
      this.activeCase = row;
      this.pay = {
        period_label: "",
        method: Number(row.is_liability) ? "Pay-in slip" : "",
        paid_at: new Date().toISOString().slice(0, 10),
        amount: 0,
      };
      this.showPay = true;
    },
    async savePay() {
      this.saving = true;
      const fd = new FormData();
      fd.append("function", "arRecordPayment");
      fd.append("case_id", String(this.activeCase.id));
      fd.append("period_label", this.pay.period_label);
      fd.append("method", this.pay.method);
      fd.append("paid_at", this.pay.paid_at);
      fd.append("amount", String(this.pay.amount));
      if (this.$refs.evidenceDocInput && this.$refs.evidenceDocInput.files[0]) {
        fd.append("evidence_doc", this.$refs.evidenceDocInput.files[0]);
      }
      try {
        const res = await execute(fd, authUrl);
        if (res?.data?.success) {
          toast.success(res.data.message || "Recorded");
          this.showPay = false;
          await this.load();
        } else {
          toast.error(res?.data?.message || "Failed");
        }
      } finally {
        this.saving = false;
      }
    },
    async openPaymentHistory(row) {
      this.activeCase = row;
      const pack = await this.fetchCase(row.id);
      if (pack?.payments) {
        this.paymentHistory = pack.payments;
        this.showPaymentHistory = true;
      } else {
        toast.error("Failed to load payments");
      }
    },
    pickFile(row, mode) {
      this.uploadId = row.id;
      this.uploadMode = mode;
      this.$refs.fileInput.value = "";
      this.$refs.fileInput.click();
    },
    async onFile(ev) {
      const file = ev.target.files && ev.target.files[0];
      if (!file || !this.uploadId) return;
      const fd = new FormData();
      if (this.uploadMode === "payin") {
        fd.append("function", "arUploadPayinSlip");
        fd.append("payin_slip", file);
      } else if (this.uploadMode === "computation") {
        fd.append("function", "arUploadComputationDoc");
        fd.append("computation_doc", file);
      } else {
        fd.append("function", "arUploadPaymentFile");
        fd.append("payment_file", file);
      }
      fd.append("case_id", String(this.uploadId));
      const res = await execute(fd, authUrl);
      if (res?.data?.success) {
        toast.success("File uploaded");
        await this.load();
      } else {
        toast.error(res?.data?.message || "Upload failed");
      }
    },
  },
};
</script>

<style scoped>
.filters-card { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; padding: 12px; margin-bottom: 12px; }
.filters-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 10px; }
.filters-grid label, .modal-card label { display: flex; flex-direction: column; gap: 4px; font-size: 12px; color: #475569; }
.hr-input, .hr-select { padding: 8px 10px; border: 1px solid #cbd5e1; border-radius: 8px; }
.table-wrap { background: #fff; border: 1px solid #e2e8f0; border-radius: 10px; overflow: auto; }
.link-btn { background: none; border: none; color: #0c4a6e; cursor: pointer; text-decoration: underline; margin-right: 6px; padding: 0; font-size: 12px; }
.modal-backdrop { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.45); z-index: 2000; display: flex; align-items: flex-start; justify-content: center; padding: 40px 16px; overflow: auto; }
.modal-card { background: #fff; border-radius: 12px; padding: 18px; width: min(560px, 100%); display: flex; flex-direction: column; gap: 10px; }
.hits { list-style: none; margin: 0; padding: 0; border: 1px solid #e2e8f0; border-radius: 8px; max-height: 180px; overflow: auto; }
.hits li { padding: 8px 10px; cursor: pointer; }
.hits li:hover { background: #f1f5f9; }
.auto-box { background: #f8fafc; border-radius: 8px; padding: 8px 10px; font-size: 13px; }
.hint { font-size: 12px; color: #475569; margin: 0; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; }
.d-none { display: none; }
</style>
